import * as React from "react";

import { cn } from "@/lib/utils";

export const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  ),
);
Table.displayName = "Table";

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
));
TableBody.displayName = "TableBody";

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /**
   * Opt-in clickable-row affordance (CHP-229): hover/focus highlight + cursor
   * for a row that opens a record. Put the open handler on the row's own
   * `onClick` (the Safari-safe cross-browser hit path — see CHP-305 note on
   * `tableRowLinkClassName`); style the primary control with
   * `tableRowLinkClassName`. Give any OTHER in-row interactive control
   * `relative z-10` (and stopPropagation on click) so it stays independently
   * clickable.
   */
  interactive?: boolean;
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, interactive, ...props }, ref) => (
    <tr
      ref={ref}
      data-interactive={interactive ? "true" : undefined}
      className={cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        interactive && "relative cursor-pointer focus-within:bg-muted/50",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

/**
 * Class for an interactive row's primary control (a Link or button). Keeps the
 * control a real, keyboard-focusable element (Enter / cmd-click on a Link still
 * work) and paints a row-spanning `::after` box for the focus/hover affordance.
 *
 * CHP-305: the overlay is `pointer-events-none` — it is VISUAL ONLY and must NOT
 * be relied on for the pointer hit-area. Safari/WebKit ignores `position:
 * relative` on a `<tr>`, so the overlay anchors to the nearest positioned
 * ancestor (the `.relative` table wrapper) instead of its own row; every row's
 * overlay then stacks over the whole table and the last one intercepts every
 * click → one fixed deal opens from any row. The cross-browser, Safari-safe
 * click path is therefore an `onClick` on the `TableRow` itself (see
 * DealsDataGrid / ManagerDealsDataGrid); in-row secondary controls keep
 * `relative z-10` + `stopPropagation` so they stay independently clickable.
 */
export const tableRowLinkClassName =
  "after:pointer-events-none after:absolute after:inset-0 after:content-['']";

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }
>(({ className, numeric, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-9 px-3 text-left align-middle font-mono text-2xs font-medium uppercase tracking-[0.08em] text-muted-foreground [&:has([role=checkbox])]:pr-0",
      // Numeric columns align right so the header sits over its digits (NN/g).
      numeric && "text-right",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }
>(({ className, numeric, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      // 40px rows (py-2.5) — the enterprise condensed norm. Numeric cells
      // right-align with tabular figures so place values stack (NN/g, Polaris).
      "px-3 py-2.5 align-middle [&:has([role=checkbox])]:pr-0",
      numeric && "text-right tabular-nums",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";
